import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import InfinityMediaItemConfigContext from '~/App/Modules/MyLibrary/Context/infinite-media-item-config-context';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faPlus } from '@fortawesome/free-solid-svg-icons';
import { DateTime } from 'luxon';

import { TagPill } from './TagPill';
import { TagPopover } from './TagPopover';
import { useTags } from '~/App/Api/Tag/hooks';
import { openEditTagsModal } from '../EditTagsModal';
import { Tag, TaggableResourcePage, TaggableResourceType } from '~/types/models/tags';
import useAnalytics from '~/react-hooks/use-analytics';
import { TAG_ADD, TAG_REMOVE } from '~/generated/AnalyticsTypes';

export interface TagBarProps {
  sourcePage: TaggableResourcePage
  resourceType?: TaggableResourceType;
  defaultTags?: Tag[];
  className?: string;
  onTagAdd?(tag: Tag): void;
  onTagDelete?(tag: Tag): void;
  emptyText?: string;
  emptyTextClassName?: string;
  tagContainerClassName?: string;
  hideAdditionalTagsCount?: boolean;
  onChange?: (tags: Tag[]) => void;
}

export function TagBar({
  defaultTags = [],
  className,
  onTagAdd,
  onTagDelete,
  resourceType,
  emptyText = 'Add a tag to manage your publishables',
  emptyTextClassName,
  tagContainerClassName,
  hideAdditionalTagsCount = false,
  sourcePage,
  onChange
}: TagBarProps) {
  const [tags, setTags] = useState(defaultTags);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [additionalTagsCount, setAdditionalTagsCount] = useState(0);
  const { data: availableTags = [] } = useTags();
  const analytics = useAnalytics();

  const closePopover = () => setPopoverOpen(false);

  const handleEditTagsClick = () => {
    closePopover();
    openEditTagsModal(sourcePage);
  };

  const itemConfig = useContext(InfinityMediaItemConfigContext);
  const { selectMode} = itemConfig;

  const handleAddTagClick = () => {
    if (!availableTags?.length) {
      handleEditTagsClick();
      return;
    }

    setPopoverOpen(!popoverOpen);
  };

  const handleTagSelect = (tag: Tag) => {
    const selected_at = DateTime.utc().toMillis();
    setTags([...tags, { ...tag, selected_at }]);
    closePopover();
    onTagAdd?.(tag);

    analytics.track(TAG_ADD, {
      context: sourcePage,
      name: tag.name,
      asset_type: resourceType
    });
  };

  const handleTagRemove = (tag: Tag) => {
    const udpatedTags = tags.filter((item) => item.uuid !== tag.uuid);
    setTags(udpatedTags);
    closePopover();
    onTagDelete?.(tag);

    analytics.track(TAG_REMOVE, {
      context: sourcePage,
      asset_type: resourceType,
      name: tag.name
    });
  };

  /**
   * Map tags to a key-value pair object
   */
  const tagMap = useMemo(() => {
    return tags.reduce<Record<string, Tag>>((tagMap, tag) => (
      { ...tagMap, [tag.uuid]: tag }
    ), {});
  }, [tags]);

  useEffect(() => {
    setTags(defaultTags);
  }, [defaultTags]);

  useEffect(() => {
    onChange?.(tags);
  }, [tags]);
  /**
   * Map available tags to indicate the pill colors
   * and group tags that are already selected.
   */
  const { selectedTags, tagList } = useMemo(() => {

    const { selectedTags, unselectedTags } = availableTags.reduce<{ selectedTags: Required<Tag>[]; unselectedTags: Tag[] }>(
      (
        { selectedTags, unselectedTags },
        tag: Tag
      ) => {
        const selectedTag = tagMap[tag.uuid];

        const tagListItem: Tag = {
          ...tag,
          color: tag.color,
          selected_at: selectedTag?.selected_at
        };

        if (tagListItem.selected_at !== undefined) {
          selectedTags.push(tagListItem as Required<Tag>);
        } else {
          unselectedTags.push(tagListItem);
        }

        return {
          selectedTags,
          unselectedTags,
        };
      }, { selectedTags: [], unselectedTags: [] }
    );

    // sort selected tags by selected_at datetime from oldest to recent
    selectedTags.sort((a, b) => a.selected_at - b.selected_at);

    return { selectedTags, tagList: [...selectedTags, ...unselectedTags] };
  }, [...availableTags, tags]);

  const tagsContainerRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const tagPills = Array.from(tagsContainerRef.current?.children ?? []) as HTMLElement[];
    const totalTagPills = tagPills.length;
    if (totalTagPills) {
      const firstElement = tagPills.shift();

      /**
       * Get the total number of visible tags by checking
       * if the top offset of the pill is the same with
       * the first pill that is currently visible.
       * */
      let visible = 1;

      for (const tagPill of tagPills) {
        const pill = tagPill;
        if (pill.offsetTop > firstElement!.offsetTop) break;
        visible++;
      }

      const count = Math.max(totalTagPills - visible, 0);

      setAdditionalTagsCount(count);
    }
  }, [tagsContainerRef.current?.offsetWidth, selectedTags]);

  const additionalTagsCountBadge = useMemo(() => {
    if (additionalTagsCount) {
      return (
        <div className='w-7 h-5 rounded-full text-neutral-700 bg-neutral-200 text-center flex items-center justify-center'>
          +{additionalTagsCount}
        </div>
      );
    }

    return null;
  }, [additionalTagsCount]);

  if (!availableTags) {
    return <></>;
  }

  return (
    <div
      className={cx(
        'w-full bg-yellow-50 rounded-[5px]',
        'flex items-center',
        className
      )}
    >
      <span className='py-[5px] pl-2.5 pr-1'>
        <FontAwesomeIcon className='text-neutral-600' icon={faTag} />
      </span>
      {selectedTags.length ? (
        <>
          <div
            className={cx(tagContainerClassName,
              'flex flex-wrap gap-x-2.5 gap-y-[5px]',
              'my-[5px] h-5 overflow-hidden flex-1'
            )}
            ref={tagsContainerRef}
          >
            {selectedTags.map((tag) => (
              <TagPill
                key={tag.uuid}
                label={tag.name}
                bgColor={tag.color}
                selected
                onDeleteClick={() => handleTagRemove(tag)}
                truncateCharactersCount={10}
              />
            ))}
          </div>
          {!hideAdditionalTagsCount && (
            <div className='w-7 h-5'>
              {additionalTagsCountBadge}
            </div>
          )}
        </>
      ) : (
        <span className={cx(emptyTextClassName, 'mx-auto text-sm text-neutral-600 text-center')}>
          {selectMode? '': emptyText}
        </span>
      )}
      {!selectMode && <TagPopover
        tagList={tagList}
        open={popoverOpen}
        onClose={closePopover}
        onEditClick={handleEditTagsClick}
        onTagSelect={handleTagSelect}
        onTagRemove={handleTagRemove}
        sourcePage={sourcePage}
      >
        <div className={cx(
          'hover:text-turquoise hover:bg-turquoise-100',
          'text-center rounded-full mx-2.5 my-[5px] w-5 h-5',
          {
            'text-neutral-600': !popoverOpen,
            'text-turquoise bg-turquoise-100': popoverOpen
          },
        )}
          onClick={handleAddTagClick}
        >
          <FontAwesomeIcon role='button' icon={faPlus} />
        </div>
      </TagPopover>
}

          </div>
  );
}
